import { useCallback, useMemo, useState } from "react";
import { Tab } from "@headlessui/react";
import StatusTabs from "components/StatusTabs";
import { ASSIGNED_LOADS_TABLE_HEADER_LIST } from "constants/haulpay-carrier";
import { ISummaryData } from "models/HaulPay";
import styles from "./style.module.scss";
import SearchInput from "../../../modules/FRListByStatusDesktop/components/SearchInput";
import DataTable from "components/DataTable";
import usePaginatedQuery from "hooks/usePaginatedQuery";
import { QUERY_CARRIER_FUNDING_REQUEST_BY_ME } from "../../ManagePay/query";
import { CARRIER_FR_STATUS_LABELS } from "constants/base";
import { usePullToRefresh } from "context/PullToRefreshContext";

type FRListByStatusDesktopProps = {
  summaryData: ISummaryData;
  refetchSummaryData: (data: any) => void;
};

type Filters = {
  loadOrInvoiceOrDebtorOrStatus?: string | undefined;
  orderBy?: string;
  status?: string;
  assignedLoads?: boolean;
};

type FiltersUpdate = Partial<Filters>;

export default function FRListByStatusDesktop({
  summaryData,
  refetchSummaryData
}: Readonly<FRListByStatusDesktopProps>) {
  const sortedSummary = useMemo(() => {
    const newSummary: ISummaryData = {};
    Object.keys(CARRIER_FR_STATUS_LABELS).forEach((key) => {
      if (summaryData[key]) {
        newSummary[key] = summaryData[key];
      }
    });
    return newSummary;
  }, [summaryData]);

  const [filters, setFilters] = useState<Filters>({
    loadOrInvoiceOrDebtorOrStatus: "",
    orderBy: "-created",
    status: Object.keys(sortedSummary)[0] || "",
    assignedLoads: true
  });

  const updateFilter = useCallback(
    (key: keyof Filters, value: Filters[keyof Filters]) => {
      setFilters((prevFilters) => {
        if (prevFilters[key] === value) {
          return prevFilters;
        }
        return {
          ...prevFilters,
          [key]: value
        };
      });
    },
    [setFilters]
  );

  const updateFilters = useCallback(
    (updates: FiltersUpdate) => {
      setFilters((prevFilters) => {
        const hasChanges = Object.keys(updates).some((key) => {
          const typedKey = key as keyof Filters;
          return prevFilters[typedKey] !== updates[typedKey];
        });

        if (!hasChanges) {
          return prevFilters;
        }

        return {
          ...prevFilters,
          ...updates
        };
      });
    },
    [setFilters]
  );

  const handleSearch = useCallback(
    (value: string | null | undefined) => {
      updateFilters({ loadOrInvoiceOrDebtorOrStatus: value ?? "", status: undefined });
      refetchSummaryData({
        assignedLoad: filters.assignedLoads,
        loadOrInvoiceOrDebtorOrStatus: value
      });
    },
    [updateFilters]
  );

  const resetSearch = useCallback(() => {
    refetchSummaryData({
      assignedLoad: filters.assignedLoads,
      loadOrInvoiceOrDebtorOrStatus: ""
    });
    updateFilters({ loadOrInvoiceOrDebtorOrStatus: "", status: Object.keys(sortedSummary)[0] });
  }, [updateFilters]);

  const {
    data,
    loading,
    totalCount,
    loadNextPage,
    loadPreviousPage,
    setPageSize,
    goToPage,
    pageInfo,
    refetchCurrentPage
  } = usePaginatedQuery<Record<string, any>, any>({
    query: QUERY_CARRIER_FUNDING_REQUEST_BY_ME,
    variables: filters,
    cursorField: "fundingRequestsByMe",
    pageSize: 20,
    options: {
      notifyOnNetworkStatusChange: true
    }
  });
  usePullToRefresh(refetchCurrentPage);
  const updatedData: any[] = [];
  for (const fr of data) {
    const factoring = fr.factoring;
    if (factoring && factoring.companyProfile) {
      updatedData.push({
        ...fr,
        brokerName: JSON.parse(factoring.companyProfile).name,
        carrierAmount: fr.paymentProfileAmount
      });
    } else {
      updatedData.push(fr);
    }
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <SearchInput
          initialValue={filters.loadOrInvoiceOrDebtorOrStatus || ""}
          onSearch={handleSearch}
          iconClassName={styles.searchIcon}
          resetSearch={resetSearch}
        />
      </div>
      <Tab.Group
        onChange={(index) =>
          updateFilter(
            "status",
            Object.keys(sortedSummary)[index] === "all"
              ? undefined
              : Object.keys(sortedSummary)[index]
          )
        }>
        <StatusTabs tabsData={sortedSummary} />

        <Tab.Panels>
          {Object.keys(sortedSummary).map((key) => (
            <Tab.Panel key={key} className="outline-none">
              <DataTable
                headers={ASSIGNED_LOADS_TABLE_HEADER_LIST}
                data={updatedData}
                sortDisableList={["id"]}
                totalCount={totalCount}
                nextLink={pageInfo.hasNextPage}
                prevLink={pageInfo.hasPreviousPage}
                setPageSize={setPageSize}
                goToPage={goToPage}
                fetchPrevPage={loadPreviousPage}
                fetchNextPage={loadNextPage}
                loading={loading}
              />
            </Tab.Panel>
          ))}
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}
